import { useEffect, useState } from "react";
import type { AssistantGeneratedArtifact } from "@maestros/contracts";
import { Markdown } from "../../components/Markdown.tsx";
import { Button } from "../../components/Button.tsx";
import { useI18n } from "../../i18n/index.tsx";

/** Owner-scoped bytes are loaded on demand; model HTML/SVG is always displayed as text. */
export function ConversationArtifact({ file }: { file: AssistantGeneratedArtifact }) {
  const { t } = useI18n();
  const [expanded, setExpanded] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [body, setBody] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const url = `/v1/assistant/attachments/${encodeURIComponent(file.id)}/content`;
  const preview = file.kind === "text" && /^(?:text\/|application\/(?:json|xml))/.test(file.mime);
  useEffect(() => {
    if (!expanded || !preview) return;
    const controller = new AbortController();
    setBody(null); setFailed(false);
    void (async () => {
      try {
        const response = await fetch(url, { credentials: "same-origin", signal: controller.signal });
        if (!response.ok) throw new Error("Artifact unavailable");
        const bytes = new Uint8Array(await response.arrayBuffer());
        if (bytes.length !== file.size_bytes || bytes.length > 262144) throw new Error("Artifact size mismatch");
        const text = new TextDecoder("utf-8", { fatal: true }).decode(bytes);
        if (!controller.signal.aborted) setBody(text);
      } catch { if (!controller.signal.aborted) setFailed(true); }
    })();
    return () => controller.abort();
  }, [expanded, preview, url, file.size_bytes, attempt]);
  return <li className="assistant-artifact" data-testid="assistant-artifact">
    <a href={url} download={file.name}>{file.name}</a>
    {file.continuation_required ? <p className="assistant-note">{t("assistant.artifact.partial")}</p> : null}
    {file.reviewed !== undefined ? <p className="assistant-note">{t(file.reviewed ? "assistant.artifact.modelReview" : "assistant.artifact.structuralReview")}</p> : null}
    {preview ? <details onToggle={event => setExpanded(event.currentTarget.open)}>
      <summary>{t("assistant.artifact.view")}</summary>
      {failed ? <div role="alert"><p>{t("assistant.artifact.failed")}</p><Button size="sm" variant="ghost" onClick={() => setAttempt(value => value + 1)}>{t("common.retry")}</Button></div>
        : body === null ? <p role="status">{t("common.loading")}</p>
        : file.mime === "text/markdown" ? <Markdown text={body} className="user-content assistant-text" /> : <pre className="user-content">{body}</pre>}
    </details> : null}
  </li>;
}
